/**
 * hoverValueFormat.ts
 * تنسيق مشترك للقيمة الأساسية واتجاه الرياح — يُستخدم في HoverValueTooltip (تمرير الفأرة)
 * وMapTapCallout (نقرة الموبايل) كي لا يتكرّر منطق التنسيق بينهما.
 */

import type { ForecastGridType } from '../../config/weatherLayers';
import { weatherService, SNOW_TEMP_MAX_C } from '../../services/weatherService';

/**
 * دالّة الترجمة تُمرَّر كوسيط بدل استيراد الـ context: هذا الملف منطق خالص يُستدعى من
 * مكوّنات ومن حلقات رسم خارج React، فلا يصحّ أن يعتمد على hook.
 */
export type Translate = (key: string) => string;

export type WindUnit = 'kmh' | 'mph' | 'ms' | 'knots';
export type PrecipUnit = 'mm' | 'inch';

const WIND_UNIT_SUFFIX: Record<WindUnit, string> = { kmh: 'km/h', mph: 'mph', knots: 'kt', ms: 'm/s' };

/**
 * أدنى معدّل هطول يُعدّ مطراً — نفس عتبة تدرّج الألوان (PRECIP_HEATMAP_STOPS تبدأ عند
 * 0.1 مم/س وما دونها شفّاف تماماً). ربطهما بعتبة واحدة مقصود: ما لا يُرسم على الخريطة
 * يجب ألّا يُقرأ رقماً في التلميح — وإلا صار المستخدم يرى «0.1 mm/h» فوق بقعة فارغة.
 */
export const PRECIP_MIN_MM_H = 0.1;

/** عدد ساعات تراكم الأمطار — أسلوب Windy، ومصدر وحيد لهذا الرقم (كان مكرَّراً محلياً
 * في HoverValueTooltip/MapTapCallout/NewWeatherMap، فوُحِّد هنا). يُستخدم في التلميح
 * (sampleAccumulated) **وفي تلوين الخريطة نفسها** (getAccumulatedFrameUrl) بنفس القيمة —
 * ضرورياً لتطابق لون الخريطة مع الرقم المعروض فوقه. */
export const PRECIP_ACCUMULATION_HOURS = 3;

/**
 * فئات شدّة الهطول وعتباتها بالمليمتر/ساعة — عتبات Zoom Earth نفسها المعتمدة في بناء
 * تدرّج الألوان (خفيف ≥0.508، معتدل ≥2.54، غزير ≥7.5، شديد ≥49.5)، فيتطابق ما يقرؤه
 * المستخدم مع اللون الذي يراه تحت المؤشّر بالضبط.
 */
const PRECIP_CLASSES: ReadonlyArray<readonly [number, string]> = [
    [49.5, 'precip.extreme'],
    [7.5, 'precip.heavy'],
    [2.54, 'precip.moderate'],
    [0.508, 'precip.light'],
    [PRECIP_MIN_MM_H, 'precip.drizzle'],
];

/**
 * نفس عتبات الشدّة لكن بمسمّيات الثلج. الهطول عند حرارة تحت الصفر يسقط ثلجاً لا مطراً،
 * وكتابة «أمطار خفيفة» فوق منطقة حرارتها ‑14° خطأ صريح يراه المستخدم فوراً. القيمة تبقى
 * مليمترات ماء مكافئ (كما يعطيها النموذج) فلا تتغيّر العتبات، الاسم فقط.
 */
const SNOW_CLASSES: ReadonlyArray<readonly [number, string]> = [
    [49.5, 'snow.extreme'],
    [7.5, 'snow.heavy'],
    [2.54, 'snow.moderate'],
    [0.508, 'snow.light'],
    [PRECIP_MIN_MM_H, 'snow.flurries'],
];

/** العتبة نفسها المستعملة في تصحيح أيقونة التوقّعات — مصدرها weatherService. */
export { SNOW_TEMP_MAX_C };

/** هل هذه الحرارة (°م) تجعل الهطول ثلجاً؟ null (لا راستر حرارة بعد) = لا نفترض ثلجاً. */
export function isSnowAtTemp(tempC: number | null | undefined): boolean {
    return typeof tempC === 'number' && Number.isFinite(tempC) && tempC <= SNOW_TEMP_MAX_C;
}

/**
 * وصف شدّة الهطول، أو 'لا هطول' تحت العتبة. يُعرض سطراً ثانياً في التلميح كما في
 * Zoom Earth: الرقم وحده لا يقول للمستخدم إن كان 3 مم/س مطراً يُذكر أم لا.
 */
export function precipitationDescription(mmPerHour: number, t: Translate, snow = false): string {
    if (!Number.isFinite(mmPerHour) || mmPerHour < PRECIP_MIN_MM_H) return t('precip.none');
    const classes = snow ? SNOW_CLASSES : PRECIP_CLASSES;
    return t(classes.find(([min]) => mmPerHour >= min)?.[1] ?? (snow ? 'snow.flurries' : 'precip.drizzle'));
}

/**
 * سطر الوصف المعروض قبل الرقم في التلميح — للهطول فقط، وفقط عند وجود مطر.
 * تحت العتبة يحمل السطر الرئيسي «لا هطول» أصلاً، فإظهار الوصف هناك تكرار حرفي.
 */
export function precipitationNote(type: ForecastGridType | null, value: number, t: Translate, snow = false): string | null {
    if (type !== 'precipitation' || value < PRECIP_MIN_MM_H) return null;
    return precipitationDescription(value, t, snow);
}

/** تنسيق معدّل الهطول بوحدة المستخدم — 'لا هطول' تحت العتبة بدل رقم صفريّ بلا معنى. */
export function formatPrecipRate(mmPerHour: number, t: Translate, unit: PrecipUnit = 'mm'): string {
    if (!Number.isFinite(mmPerHour) || mmPerHour < PRECIP_MIN_MM_H) return t('precip.none');
    if (unit === 'inch') {
        const inches = mmPerHour / 25.4;
        // بالإنش تصير الأرقام صغيرة جداً (0.1 مم/س = 0.004 إنش/س) فتحتاج ثلاث خانات.
        return `${inches.toFixed(inches < 0.1 ? 3 : 2)} in/h`;
    }
    // خانة عشرية واحدة تكفي حتى 10 مم/س؛ فوقها الكسر ضوضاء أمام حجم الرقم.
    return `${mmPerHour >= 10 ? Math.round(mmPerHour) : mmPerHour.toFixed(1)} mm/h`;
}

/**
 * تنسيق تراكم الأمطار لثلاث ساعات — أسلوب Windy (رقم رئيسي بالمليمتر + عنوان فرعي
 * "الأمطار (3h)")، بدل معدّل لحظي (مم/س). العتبة نفسها PRECIP_MIN_MM_H (تحت 0.1 مم
 * تراكمياً يعني عملياً لا هطول). القيمة المُدخلة هنا يجب أن تكون التراكم (راجع
 * rasterSampler.sampleAccumulated) لا معدّل ساعة واحدة.
 */
export function formatPrecipAccumulated(mmTotal: number, t: Translate, unit: PrecipUnit = 'mm', snow = false): { main: string; sub: string } {
    const sub = t(snow ? 'snow.accum3h' : 'precip.accum3h');
    if (!Number.isFinite(mmTotal) || mmTotal < PRECIP_MIN_MM_H) return { main: t('precip.none'), sub };
    if (unit === 'inch') {
        const inches = mmTotal / 25.4;
        return { main: `${inches.toFixed(inches < 0.1 ? 3 : 2)} in`, sub };
    }
    return { main: `${mmTotal >= 10 ? Math.round(mmTotal) : mmTotal.toFixed(1)} mm`, sub };
}

/**
 * تنسيق طبقات **الشذوذ** (‏sst-anomaly وsst-anomaly-rel وt850-anomaly):
 * قيمتها فرقٌ عن معدّل مناخي لا قياس مطلق.
 *
 * وهذا يغيّر شيئين في التنسيق لا شيئاً واحداً:
 *
 *  ١. **الإشارة تُكتب دائماً.** «+2» و«−2» طرفا نقيض هنا (أدفأ/أبرد من المعتاد)،
 *     و«2» وحدها لا تقول أيّهما. في القياس المطلق الإشارة زائدة، وفي الفرق هي نصف
 *     المعلومة.
 *  ٢. **التحويل إلى فهرنهايت بلا إزاحة الـ32.** فرق درجة واحدة مئوية يساوي 1.8 فهرنهايت،
 *     لا 33.8. تمرير فرقٍ عبر معادلة القياس المطلق يُنتج رقماً بعيداً تماماً عن الصواب
 *     (‏شذوذ +2 °م كان سيُعرض «+36 °ف»)، وهو خطأ لا يلفت النظر لأنه يبدو رقماً معقولاً.
 */
function formatTempDelta(deltaC: number, fahrenheit: boolean): string {
    const v = fahrenheit ? deltaC * 9 / 5 : deltaC;
    const r = Math.round(v * 10) / 10;
    // صفرٌ سالب (‏-0.04 → «-0.0») يوحي ببرودة ليست في الرقم؛ Object.is يلتقط ‎-0 بعد التقريب.
    const shown = Object.is(r, -0) ? 0 : r;
    return `${shown > 0 ? '+' : ''}${shown.toFixed(1)}°${fahrenheit ? 'F' : 'C'}`;
}

/** تنسيق سرعة الرياح (المصدر دائماً km/h) بوحدة المستخدم المختارة من الإعدادات. */
export function formatWindSpeed(speedKmh: number, unit: WindUnit): string {
    const converted = weatherService.convertWindSpeed(speedKmh, unit);
    return `${Math.round(converted)} ${WIND_UNIT_SUFFIX[unit]}`;
}

/** تنسيق القيمة الأساسية بوحدتها (مع تحويل الحرارة لفهرنهايت عند الحاجة). */
export function formatMainValue(type: ForecastGridType, value: number, fahrenheit: boolean, t: Translate, windUnit: WindUnit = 'kmh', precipUnit: PrecipUnit = 'mm'): string {
    switch (type) {
        case 'temperature':
        case 'feels-like':
        case 'dewpoint':
        // اللمبة الرطبة حرارة كسائر الحرارات، وكانت تسقط إلى الحالة الافتراضية فتُعرض
        // رقماً عارياً بلا وحدة ولا تحويل لفهرنهايت — بينما هي الطبقة التي تُقرأ عتباتها
        // بالدرجة تحديداً (‏31 °م حدّ الخطر على الإنسان).
        case 'wet-bulb': {
            const v = fahrenheit ? value * 9 / 5 + 32 : value;
            return `${Math.round(v)}°${fahrenheit ? 'F' : 'C'}`;
        }
        case 'wind':
        case 'wind-gusts':
            return formatWindSpeed(value, windUnit);
        case 'precipitation':
            return formatPrecipRate(value, t, precipUnit);
        case 'radar-sim':
            return `${Math.round(value)} dBZ`;
        case 'pressure':
            return `${Math.round(value)} hPa`;
        case 'humidity':
        case 'clouds':
            return `${Math.round(value)}%`;
        case 'sst-anomaly':
        case 'sst-anomaly-rel':
        case 't850-anomaly':
            return formatTempDelta(value, fahrenheit);
        case 'k-index':
            /*
             * **‏°م ولو كان المؤشّر «رقماً قياسياً».** صيغته مجموع فروق حرارة:
             * ‏(T850−T500) + Td850 − (T700−Td700)، وكلّها بالمئوية — فوحدته مئوية فعلاً
             * لا مجازاً، وهكذا تكتبها المراجع: «K-Index (°C)». الرقم العاري كان يترك
             * القارئ بلا سلّم يقيس عليه «34».
             *
             * **ولا يُحوَّل إلى فهرنهايت** ولو اختار المستخدم فهرنهايت في الإعدادات:
             * عتبات هذا المؤشّر (‏20، 25، 30، 35، 40) عالمية بالمئوية، وشريط المفتاح
             * تحت الخريطة مرقّم بها. تحويل الرقم وحده كان سيجعل التلميح يقول «93»
             * بينما المفتاح الذي يشرحه يقول «35» — تناقض ظاهر على شاشة واحدة.
             */
            return `${Math.round(value)}°C`;
        case 'o-index':
            // كثافة صواعق: كسور صغيرة مهمّة قرب العتبة الأولى (0.025)، فمنزلتان عشريتان
            // تحت الواحد ومنزلة واحدة فوقه — تماماً كما يعرضها Windy (‏0.29 l/km²).
            return value < 1 ? `${value.toFixed(2)} l/km²` : `${value.toFixed(1)} l/km²`;
        default:
            return `${Math.round(value)}`;
    }
}

/**
 * تنسيق مختصر لشارة تسمية المدينة (المستطيل الصغير تحت الاسم) — لا مساحة فيه لاسم
 * الوحدة كاملاً، فنكتفي بالرمز حيث يكون قصيراً (° و%) ونحذفه حيث يطول (km/h، hPa، mm)
 * لأن مفتاح الألوان أسفل الخريطة يوضّح وحدة الطبقة الفعّالة أصلاً.
 */
export function formatBadgeValue(type: ForecastGridType, value: number, fahrenheit: boolean, windUnit: WindUnit = 'kmh'): string {
    switch (type) {
        case 'temperature':
        case 'feels-like':
        case 'wet-bulb':
        case 'dewpoint':
            return `${Math.round(fahrenheit ? value * 9 / 5 + 32 : value)}°`;
        case 'wind':
        case 'wind-gusts':
            return `${Math.round(weatherService.convertWindSpeed(value, windUnit))}`;
        case 'humidity':
        case 'clouds':
            return `${Math.round(value)}%`;
        // مؤشّر K: نفس كمّية التلميح (°م) بالرمز القصير الذي يسعه المستطيل.
        case 'k-index':
            return `${Math.round(value)}°`;
        // الشذوذ: الرمز «°» قصير فيسعه المستطيل، والإشارة **إلزامية** لا تجميلية —
        // «2» و«‎-2» في هذه الطبقات معنيان متضادّان (أدفأ/أبرد من المعتاد)، وكان
        // الرقم يُعرض بلا إشارة موجبة فيلتبس الدفء الشاذّ بالبرد الشاذّ.
        case 'sst-anomaly':
        case 'sst-anomaly-rel':
        case 't850-anomaly': {
            const v = Math.round(fahrenheit ? value * 9 / 5 : value);
            return `${v > 0 ? '+' : ''}${Object.is(v, -0) ? 0 : v}°`;
        }
        case 'precipitation':
            // شارة المدينة صغيرة ولا تسع كلمة: شرطة تعني «لا هطول» أوضح من «0.0».
            return value < PRECIP_MIN_MM_H ? '—' : (value >= 10 ? `${Math.round(value)}` : value.toFixed(1));
        default:
            return `${Math.round(value)}`;
    }
}

const CARDINALS = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
                   'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];

/** يحوّل زاوية اتجاه الرياح (مصدرها، بالدرجات) إلى جهة من 16 جهة بلغة الواجهة. */
export function windDirToCardinal(deg: number, t: Translate): string {
    return t(`compass.${CARDINALS[Math.round((((deg % 360) + 360) % 360) / 22.5) % 16]}`);
}
